import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import '../ExpensesPage/ExpensesPage.css';
import { 
  categoryService, 
  receiptService 
} from '../../../apiServices';
import { 
  Category, 
  CreatePositionRequest, 
  CreateCategoryRequest,
  CurrentList,
  Receipt
} from '../../../apiServices/types';
import { ShoppingCategory } from '../../../types';
import {
  ShoppingListHeader,
  CategoryCard,
  CategoryCardRef,
  ExpensesSummary
} from '../ExpensesPage/components';
import { syncService } from '../../../services/sync';
import { SyncReceiptRequest } from '../../../services/sync/types';
import { SyncStatusIndicator, SyncErrorsPanel, VersionWarning } from '../../SyncStatus';
import { useNotification } from '../../../contexts/NotificationContext';
import { useRefresh } from '../../../contexts/RefreshContext';

const ShoppingListPage: React.FC = () => {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  
  const [currentList, setCurrentList] = useState<CurrentList>({
    name: '',
    status: 'planning',
    categories: {},
    accountId: undefined,
    receiptId: receiptId
  });
  const [priceMode, setPriceMode] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [receiptStatus, setReceiptStatus] = useState<unknown>(undefined);
  const categoryCardRefs = useRef<Record<string, CategoryCardRef | null>>({});
  
  const [isAddingItem, setIsAddingItem] = useState<Record<string, boolean>>({});
  const [isRemovingItem, setIsRemovingItem] = useState<Record<string, boolean>>({});
  const [isTogglingItem, setIsTogglingItem] = useState<Record<string, boolean>>({});
  const [isUpdatingPrice, setIsUpdatingPrice] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAddingCategory, setIsAddingCategory] = useState<boolean>(false);
  const [receiptDate, setReceiptDate] = useState<string>('');
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const [isRestoring, setIsRestoring] = useState<boolean>(false);
  
  const priceUpdateTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const previousListRef = useRef<CurrentList | null>(null);
  const isInitialMount = useRef<boolean>(true);
  const [clientVersion, setClientVersion] = useState<number>(0);
  const [serverVersion, setServerVersion] = useState<number>(0);
  const [showVersionWarning, setShowVersionWarning] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { showNotification } = useNotification();

  // Налаштувати NotificationContext для syncService
  useEffect(() => {
    syncService.setNotificationCallback(showNotification);
  }, [showNotification]);

  /**
   * Конвертувати CurrentList в SyncReceiptRequest для синхронізації
   */
  const convertToSyncRequest = useCallback((): SyncReceiptRequest | null => {
    if (!receiptId || !currentList.accountId) {
      return null;
    }

    // Зібрати всі позиції з категорій (мінімальний набір полів)
    const positions: SyncReceiptRequest['positions'] = [];
    
    Object.values(currentList.categories).forEach(category => {
      category.items.forEach(item => {
        positions.push({
          id: item.positionId, // client-generated UUID для нових або server ID для існуючих
          name: item.name,
          amount: item.price || 0,
          isBought: item.checked || false,
          categoryId: category.apiCategoryId
        });
      });
    });

    // Визначити, чи змінилися name або accountId
    const previousList = previousListRef.current;
    const nameChanged = !previousList || previousList.name !== currentList.name;
    const accountIdChanged = !previousList || previousList.accountId !== currentList.accountId;

    // Мінімальний об'єкт для синхронізації (без _sync метаданих)
    const syncRequest: SyncReceiptRequest = {
      ...(nameChanged && { name: currentList.name }),
      ...(accountIdChanged && { accountId: currentList.accountId }),
      positions
    };

    return syncRequest;
  }, [currentList, receiptId]);

  // Почати polling версії при завантаженні receipt
  useEffect(() => {
    if (!receiptId || isLoading) return;
    
    const handleVersionConflict = (serverVersion: number) => {
      setServerVersion(serverVersion);
      setShowVersionWarning(true);
    };
    
    // Callback для синхронізації коли clientVersion > serverVersion
    const handleSyncNeeded = async () => {
      if (isDirty) {
        const syncRequest = convertToSyncRequest();
        if (syncRequest) {
          return syncRequest;
        }
      }
      // Перевірити чи є pending changes в localStorage
      const pendingReceipt = syncService.getPendingChanges(receiptId);
      return pendingReceipt || null;
    };
    
    // Callback для оновлення версій та статусу після успішної синхронізації
    const handleSyncSuccess = (syncedReceipt: Receipt) => {
      setClientVersion(syncedReceipt.version);
      setServerVersion(syncedReceipt.version);
      setIsDirty(false);
      // Оновити тільки статус з бекенду
      setReceiptStatus(syncedReceipt.status);
    };
    
    syncService.startPolling(receiptId, () => clientVersion, handleVersionConflict, handleSyncNeeded, handleSyncSuccess);
    
    return () => {
      syncService.stopPolling(receiptId);
    };
  }, [receiptId, isLoading, clientVersion, isDirty, convertToSyncRequest]);

  const refreshReceiptStatus = useCallback(async (): Promise<void> => {
    if (!receiptId) return;
    
    try {
      const receipt = await receiptService.getById(receiptId);
      setReceiptStatus(receipt.status);
      setIsDeleted(receipt.isDeleted || false);
    } catch (error) {
    }
  }, [receiptId]);

  useEffect(() => {
    const loadData = async () => {
      if (!receiptId) return;
      
      try {
        setIsLoading(true);
        const receipt = await receiptService.getById(receiptId);
        
        // Перевірити чи є pending changes в localStorage
        const pendingVersion = syncService.getPendingClientVersion(receiptId);
        
        // Встановити версію з сервера або з pending changes якщо є
        setServerVersion(receipt.version);
        setClientVersion(pendingVersion !== null && pendingVersion !== undefined ? pendingVersion : receipt.version);
        setIsDirty(pendingVersion !== null && pendingVersion !== undefined);
        
        const categories: Record<string, ShoppingCategory> = {};
        
        receipt.positions.forEach(position => {
          const categoryKey = position.categoryId || 'default';
          
          if (!categories[categoryKey]) {
            categories[categoryKey] = {
              name: position.categoryName || 'Без категорії',
              items: [],
              apiCategoryId: position.categoryId
            };
          }
          
          const itemId = position.id || uuidv4();
          categories[categoryKey].items.push({
            id: itemId,
            name: position.name,
            checked: position.isBought,
            price: position.amount,
            positionId: itemId,
            createdDate: position.createdDate
          });
        });

        let defaultCategoryId: string | null = null;
        if (receipt.positions.length === 0) {
          defaultCategoryId = `cat_${Date.now()}`;
          categories[defaultCategoryId] = {
            name: '',
            items: []
          };
        }
        
        const loadedList: CurrentList = {
          name: receipt.name,
          status: receipt.positions.length === 0 ? 'planning' : 'shopping', 
          categories,
          accountId: receipt.accountId,
          receiptId: receipt.id
        };
        
        setCurrentList(loadedList);
        
        // Оновити previousListRef після завантаження, щоб не синхронізувати завантажені дані
        previousListRef.current = { ...loadedList };
        isInitialMount.current = false;
        
        setReceiptStatus(receipt.status);
        setReceiptDate(receipt.date);
        setIsDeleted(receipt.isDeleted || false);

        if (defaultCategoryId) {
          setTimeout(() => {
            categoryCardRefs.current[defaultCategoryId]?.focusCategory();
          }, 200);
        }
      } catch (error) {
        setError('Помилка завантаження чека: ' + (error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    const loadCategories = async (): Promise<void> => {
      try {
        const data = await categoryService.getAll();
        setCategories(data);
      } catch (error) {
      }
    };

    if (receiptId) {
      loadData();
      loadCategories();
    }
  }, [receiptId]);

  // Обробка конфлікту версій - оновити дані з сервера
  const handleRefreshData = useCallback(async () => {
    if (!receiptId) return;
    
    try {
      const receipt = await receiptService.getById(receiptId);
      
      setServerVersion(receipt.version);
      setClientVersion(receipt.version);
      setIsDirty(false);
      setShowVersionWarning(false);
      
      const categories: Record<string, ShoppingCategory> = {};
      
      receipt.positions.forEach(position => {
        const categoryKey = position.categoryId || 'default';
        
        if (!categories[categoryKey]) {
          categories[categoryKey] = {
            name: position.categoryName || 'Без категорії',
            items: [],
            apiCategoryId: position.categoryId
          };
        }
        
        const itemId = position.id || uuidv4();
        categories[categoryKey].items.push({
          id: itemId,
          name: position.name,
          checked: position.isBought,
          price: position.amount,
          positionId: itemId,
          createdDate: position.createdDate
        });
      });

      const updatedList: CurrentList = {
        name: receipt.name,
        status: receipt.positions.length === 0 ? 'planning' : (receipt.status === 1 ? 'shopping' : 'planning'),
        categories,
        accountId: receipt.accountId,
        receiptId: receipt.id
      };
      
      setCurrentList(updatedList);
      previousListRef.current = { ...updatedList };
      setReceiptStatus(receipt.status);
      setIsDeleted(receipt.isDeleted || false);
    } catch (error) {
      console.error('Помилка оновлення даних:', error);
      showNotification('Не вдалося оновити дані', 'error');
    }
  }, [receiptId, showNotification]);

  useRefresh(handleRefreshData);

  useEffect(() => {
    const timers = priceUpdateTimers.current;
    return () => {
      Object.values(timers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const togglePriceMode = (): void => {
    if (isDeleted) return; // Блокуємо зміну режиму для видалених чеків
    setPriceMode(!priceMode);
  };

  const handleRestore = async (): Promise<void> => {
    if (!receiptId || isRestoring) return;
    
    setIsRestoring(true);
    try {
      await receiptService.restore(receiptId);
      setIsDeleted(false);
      // Синхронізація відбудеться автоматично через useEffect
    } catch (error) {
      setError('Помилка відновлення чека: ' + (error as Error).message);
    } finally {
      setIsRestoring(false);
    }
  };

  const addNewCategoryBlock = (): void => {
    if (isAddingCategory || isDeleted) return; // Блокуємо додавання категорій для видалених чеків 
    
    setIsAddingCategory(true);
    
    try {
      const categoryId = `cat_${Date.now()}`;
      setCurrentList(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          [categoryId]: {
            name: '',
            items: []
          }
        }
      }));

      setTimeout(() => {
        categoryCardRefs.current[categoryId]?.focusCategory();
      }, 100);
    } finally {
      setTimeout(() => setIsAddingCategory(false), 300);
    }
  };

  const updateCategoryName = async (categoryId: string, name: string): Promise<void> => {
    if (isDeleted) return; // Блокуємо редагування для видалених чеків
    setCurrentList(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryId]: {
          ...prev.categories[categoryId],
          name
        }
      }
    }));

    const currentCategory = currentList.categories[categoryId];
    const isNewEmptyCategory = currentCategory && currentCategory.items.length === 0 && !currentCategory.apiCategoryId;
    
    if (name.trim() && categories.find(c => c.name.toLowerCase() === name.toLowerCase()) && isNewEmptyCategory) {
      setTimeout(() => {
        categoryCardRefs.current[categoryId]?.focusAddItem();
      }, 100);
    }

    if (name.trim()) {
      try {
        const existingCategory = categories.find(c => c.name.toLowerCase() === name.toLowerCase());
        let newApiCategoryId: string | undefined;
        
        if (!existingCategory) {
          const newCategoryRequest: CreateCategoryRequest = { 
            name: name.trim()
          };
          const createdCategory = await categoryService.create(newCategoryRequest);
          
          setCategories(prev => [...prev, createdCategory]);
          newApiCategoryId = createdCategory.id;
        } else {
          newApiCategoryId = existingCategory.id;
        }
        
        setCurrentList(prev => ({
          ...prev,
          categories: {
            ...prev.categories,
            [categoryId]: {
              ...prev.categories[categoryId],
              apiCategoryId: newApiCategoryId
            }
          }
        }));
        
        // Зміни збережуться автоматично через useEffect
        
        if (isNewEmptyCategory) {
          setTimeout(() => {
            categoryCardRefs.current[categoryId]?.focusAddItem();
          }, 100);
        }
      } catch (error) {
        setError('Помилка створення категорії: ' + (error as Error).message);
      }
    }
  };

  const removeCategory = (categoryId: string): void => {
      setCurrentList(prev => {
        const newCategories = { ...prev.categories };
        delete newCategories[categoryId];
        return {
          ...prev,
          categories: newCategories
        };
      });
  };

  const addItem = async (event: React.FormEvent<HTMLFormElement>, categoryId: string): Promise<void> => {
    event.preventDefault();
    if (isDeleted) return; // Блокуємо додавання товарів для видалених чеків
    const formData = new FormData(event.currentTarget);
    const itemName = formData.get('item-name') as string;

    if (!itemName || !receiptId) return;
    
    if (isAddingItem[categoryId]) return;
    
    setIsAddingItem(prev => ({ ...prev, [categoryId]: true }));

    try {
      const category = currentList.categories[categoryId];
      let apiCategoryId = category?.apiCategoryId;

      if (category?.name && !apiCategoryId) {
        const allCategories = await categoryService.getAll();

        let existingCategory = allCategories.find(c => c.name.toLowerCase() === category.name.toLowerCase());
        if (!existingCategory) {
          const newCategoryRequest: CreateCategoryRequest = { 
            name: category.name
          };
          existingCategory = await categoryService.create(newCategoryRequest);
        }
        apiCategoryId = existingCategory?.id;
        
        setCurrentList(prev => ({
          ...prev,
          categories: {
            ...prev.categories,
            [categoryId]: {
              ...prev.categories[categoryId],
              apiCategoryId
            }
          }
        }));
      }

      // Додати позицію локально з client-generated UUID
      const positionId = uuidv4();
      setCurrentList(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          [categoryId]: {
            ...prev.categories[categoryId],
            items: [
              ...prev.categories[categoryId].items,
              {
                id: positionId,
                positionId: positionId,
                name: itemName,
                checked: false,
                price: 0,
                createdDate: new Date().toISOString()
              }
            ]
          }
        }
      }));

      // Зміни збережуться автоматично через useEffect
      event.currentTarget.reset();
    } catch (error) {
      setError('Помилка додавання товару: ' + (error as Error).message);
    } finally {
      setIsAddingItem(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const toggleItem = async (categoryId: string, itemId: string): Promise<void> => {
    if (isDeleted) return; // Блокуємо зміну статусу товарів для видалених чеків
    const category = currentList.categories[categoryId];
    const item = category?.items.find(i => i.id === itemId);
    
    if (!item || !receiptId) return;
    
    const toggleKey = `${categoryId}_${itemId}`;
    if (isTogglingItem[toggleKey]) return;
    
    setIsTogglingItem(prev => ({ ...prev, [toggleKey]: true }));

    const newCheckedState = !item.checked;

    // Оновити локально - зміни збережуться автоматично через useEffect
    setCurrentList(prev => {
      const newCategories = { ...prev.categories };
      const categoryToUpdate = { ...newCategories[categoryId] };
      // Створити новий масив items з оновленим елементом
      categoryToUpdate.items = categoryToUpdate.items.map(i => 
        i.id === itemId ? { ...i, checked: newCheckedState } : i
      );
      newCategories[categoryId] = categoryToUpdate;
      return {
        ...prev,
        categories: newCategories
      };
    });

    setIsTogglingItem(prev => ({ ...prev, [toggleKey]: false }));
  };

  const updateItemPrice = useCallback((categoryId: string, itemId: string, price: string): void => {
    if (isDeleted) return; // Блокуємо зміну цін для видалених чеків
    const category = currentList.categories[categoryId];
    const item = category?.items.find(i => i.id === itemId);
    
    if (!item || !receiptId) return;

    const newAmount = parseFloat(price) || 0;
    
    // Оновлюємо локальний стан одразу - зміни збережуться автоматично через useEffect
    setCurrentList(prev => {
      const newCategories = { ...prev.categories };
      const categoryToUpdate = { ...newCategories[categoryId] };
      // Створити новий масив items з оновленим елементом
      categoryToUpdate.items = categoryToUpdate.items.map(i => 
        i.id === itemId ? { ...i, price: newAmount } : i
      );
      newCategories[categoryId] = categoryToUpdate;
      return {
        ...prev,
        categories: newCategories
      };
    });
  }, [currentList.categories, receiptId, isDeleted]);

  const updateItemName = useCallback((categoryId: string, itemId: string, name: string): void => {
    if (isDeleted) return; // Блокуємо зміну назв для видалених чеків
    const category = currentList.categories[categoryId];
    const item = category?.items.find(i => i.id === itemId);
    
    if (!item || !receiptId || !name.trim()) return;
    
    // Оновлюємо локальний стан одразу - зміни збережуться автоматично через useEffect
    setCurrentList(prev => {
      const newCategories = { ...prev.categories };
      const categoryToUpdate = { ...newCategories[categoryId] };
      // Створити новий масив items з оновленим елементом
      categoryToUpdate.items = categoryToUpdate.items.map(i => 
        i.id === itemId ? { ...i, name: name.trim() } : i
      );
      newCategories[categoryId] = categoryToUpdate;
      return {
        ...prev,
        categories: newCategories
      };
    });
  }, [currentList.categories, receiptId, isDeleted]);

  const saveItemPriceImmediately = useCallback(async (categoryId: string, itemId: string): Promise<void> => {
    // Зміни зберігаються автоматично через useEffect з debounce
    // Цей метод залишається для сумісності, але не робить нічого додаткового
  }, []);

  const removeItem = async (categoryId: string, itemId: string): Promise<void> => {
    if (isDeleted) return; // Блокуємо видалення товарів для видалених чеків
    const category = currentList.categories[categoryId];
    const item = category?.items.find(i => i.id === itemId);
    
    if (!item || !receiptId) return;
    
    const removeKey = `${categoryId}_${itemId}`;
    if (isRemovingItem[removeKey]) return;
    
    setIsRemovingItem(prev => ({ ...prev, [removeKey]: true }));

    // Видалити локально - зміни збережуться автоматично через useEffect
    // Позиція буде видалена на сервері під час синхронізації (через відсутність в масиві positions)
    setCurrentList(prev => {
      const newCategories = { ...prev.categories };
      const items = newCategories[categoryId].items.filter(i => i.id !== itemId);
      newCategories[categoryId] = {
        ...newCategories[categoryId],
        items
      };
      return {
        ...prev,
        categories: newCategories
      };
    });
    
    setIsRemovingItem(prev => ({ ...prev, [removeKey]: false }));
  };

  const calculateTotals = (): { categoryTotals: Record<string, number>; grandTotal: number } => {
    let grandTotal = 0;
    const categoryTotals: Record<string, number> = {};

    Object.entries(currentList.categories).forEach(([categoryId, category]) => {
      let categoryTotal = 0;
      category.items.forEach(item => {
        if (item.checked) {
          categoryTotal += item.price || 0;
        }
      });
      categoryTotals[categoryId] = categoryTotal;
      grandTotal += categoryTotal;
    });

    return { categoryTotals, grandTotal };
  };

  /**
   * Зберегти зміни локально (в localStorage якщо офлайн)
   * Синхронізація відбудеться автоматично через polling коли clientVersion > serverVersion
   */
  const saveChanges = useCallback(() => {
    if (!receiptId || isDeleted) return;

    const syncRequest = convertToSyncRequest();
    if (syncRequest && receiptId) {
      // Зберегти зміни в localStorage (для офлайн режиму)
      // Синк відбудеться автоматично через polling коли clientVersion > serverVersion
      syncService.savePendingChanges(receiptId, syncRequest, clientVersion);
      
      // Оновити previousListRef
      previousListRef.current = { ...currentList };
    }
  }, [receiptId, convertToSyncRequest, isDeleted, currentList, clientVersion]);

  // Автоматично зберігати зміни при оновленні currentList (тільки якщо є реальні зміни)
  useEffect(() => {
    // Не синхронізувати під час завантаження даних
    if (isLoading) {
      return;
    }

    // Якщо немає receiptId, не синхронізувати
    if (!receiptId || !currentList.receiptId) {
      return;
    }

    // Перевірити, чи дійсно були зміни
    const previousList = previousListRef.current;
    if (!previousList) {
      // Перший раз - зберегти поточний стан як базовий
      previousListRef.current = { ...currentList };
      return;
    }

    // Перевірити, чи змінився receiptId (новий список)
    if (previousList.receiptId !== currentList.receiptId) {
      // Новий список - оновити базовий стан
      previousListRef.current = { ...currentList };
      return;
    }

    // Перевірити зміни в name та accountId
    const nameOrAccountChanged = 
      previousList.name !== currentList.name ||
      previousList.accountId !== currentList.accountId;

    // Перевірити, чи є реальні зміни в позиціях (товарах)
    // Зібрати всі позиції з попереднього стану
    const previousPositions: Array<{ id?: string; name: string; amount: number; isBought: boolean; categoryId?: string }> = [];
    Object.values(previousList.categories).forEach(category => {
      category.items.forEach(item => {
        previousPositions.push({
          id: item.positionId,
          name: item.name,
          amount: item.price || 0,
          isBought: item.checked || false,
          categoryId: category.apiCategoryId
        });
      });
    });

    // Зібрати всі позиції з поточного стану
    const currentPositions: Array<{ id?: string; name: string; amount: number; isBought: boolean; categoryId?: string }> = [];
    Object.values(currentList.categories).forEach(category => {
      category.items.forEach(item => {
        currentPositions.push({
          id: item.positionId,
          name: item.name,
          amount: item.price || 0,
          isBought: item.checked || false,
          categoryId: category.apiCategoryId
        });
      });
    });

    // Порівняти позиції (ігноруючи порядок)
    const positionsChanged = JSON.stringify([...previousPositions].sort((a, b) => (a.id || '').localeCompare(b.id || ''))) !== 
                             JSON.stringify([...currentPositions].sort((a, b) => (a.id || '').localeCompare(b.id || '')));

    // Синхронізувати тільки якщо є реальні зміни в позиціях або в name/accountId
    const hasRealChanges = nameOrAccountChanged || positionsChanged;

    if (hasRealChanges && (currentList.name || Object.keys(currentList.categories).length > 0) && !isDeleted) {
      // Позначити як dirty та збільшити локальну версію
      setIsDirty(true);
      setClientVersion(prev => prev + 1);
      
      // Відправити зміни на сервер
      saveChanges();
      
      // Оновити previousListRef після збереження
      previousListRef.current = { ...currentList };
    } else {
      // Якщо змін немає, оновлюємо previousListRef, щоб уникнути зайвих перевірок
      previousListRef.current = { ...currentList };
    }
  }, [currentList.name, currentList.accountId, currentList.categories, currentList.receiptId, currentList.status, receiptId, saveChanges, isDeleted, isLoading]);

  const submitReceipt = async (): Promise<void> => {
    if (isSubmitting) return;
    
    const hasItems = Object.values(currentList.categories).some(cat => cat.items.length > 0);
    if (!hasItems) {
      setError('Додайте хоча б один товар');
      return;
    }

    setIsSubmitting(true);

    try {
      const hasPrices = Object.values(currentList.categories)
        .some(cat => cat.items.some(item => item.price > 0));

      if (hasPrices && receiptId) {
        await receiptService.submit(receiptId);
        // Синхронізація відбудеться автоматично через useEffect
      }

      navigate('/dashboard/expenses');
    } catch (error) {
      setError('Помилка збереження чека: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { categoryTotals, grandTotal } = calculateTotals();

  if (isLoading) {
    return (
      <div className="expenses-page">
        <div className="card">
          <h2>Завантаження...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="expenses-page">
      {showVersionWarning && (
        <VersionWarning
          serverVersion={serverVersion}
          onRefresh={handleRefreshData}
          onDismiss={() => setShowVersionWarning(false)}
        />
      )}
      <SyncStatusIndicator receiptId={receiptId} clientVersion={clientVersion} serverVersion={serverVersion} />
      <SyncErrorsPanel />
      
      <div className="shopping-section">
        <ShoppingListHeader
          listName={currentList.name}
          status={currentList.status}
          priceMode={priceMode}
          receiptStatus={receiptStatus}
          receiptId={receiptId}
          receiptDate={receiptDate}
          accountId={currentList.accountId}
          accountName={undefined}
          onTogglePriceMode={togglePriceMode}
          isDeleted={isDeleted}
          onDateUpdate={async () => {
            if (receiptId) {
              const receipt = await receiptService.getById(receiptId);
              setReceiptDate(receipt.date);
              setCurrentList(prev => ({
                ...prev,
                name: receipt.name,
                accountId: receipt.accountId
              }));
            }
          }}
          onAccountUpdate={async () => {
            if (receiptId) {
              const receipt = await receiptService.getById(receiptId);
              setCurrentList(prev => ({
                ...prev,
                accountId: receipt.accountId
              }));
            }
          }}
        />

        {error && <div className="alert alert-error show">{error}</div>}

        {isDeleted && (
          <div className="alert alert-warning show deleted-receipt-banner">
            <div className="deleted-banner-content">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>Цей чек видалено. Редагування недоступне.</span>
              <button 
                className="btn btn-primary btn-sm restore-btn" 
                onClick={handleRestore}
                disabled={isRestoring}
              >
                {isRestoring ? 'Відновлення...' : 'Відновити чек'}
              </button>
            </div>
          </div>
        )}

        <div className={`categories-container ${isDeleted ? 'deleted-receipt' : ''}`}>
          {Object.entries(currentList.categories)
            .sort(([, categoryA], [, categoryB]) => {
              // Знайти найранішу дату створення в кожній категорії
              const getEarliestDate = (category: ShoppingCategory) => {
                if (!category.items || category.items.length === 0) {
                  // Порожні категорії вважаються найновішими - використовуємо поточну дату
                  return Date.now();
                }
                const dates = category.items
                  .map(item => item.createdDate ? new Date(item.createdDate).getTime() : 0)
                  .filter(date => date > 0);
                return dates.length > 0 ? Math.min(...dates) : Date.now();
              };
              
              const dateA = getEarliestDate(categoryA);
              const dateB = getEarliestDate(categoryB);
              
              // Сортувати по даті (найновіші внизу)
              return dateA - dateB;
            })
            .map(([categoryId, category]) => (
              <CategoryCard
                key={categoryId}
                ref={(ref) => { categoryCardRefs.current[categoryId] = ref; }}
                categoryId={categoryId}
                category={category}
                categories={categories}
                priceMode={priceMode}
                onUpdateName={(name: string) => updateCategoryName(categoryId, name)}
                onRemove={() => removeCategory(categoryId)}
                onAddItem={(event: React.FormEvent<HTMLFormElement>) => addItem(event, categoryId)}
                onToggleItem={(itemId: string) => toggleItem(categoryId, itemId)}
                onUpdatePrice={(itemId: string, price: string) => updateItemPrice(categoryId, itemId, price)}
                onUpdateItemName={(itemId: string, name: string) => updateItemName(categoryId, itemId, name)}
                onSavePrice={(itemId: string) => saveItemPriceImmediately(categoryId, itemId)}
                onRemoveItem={(itemId: string) => removeItem(categoryId, itemId)}
                total={categoryTotals[categoryId] || 0}
                isAddingItem={isAddingItem[categoryId] || false}
                isRemovingItems={Object.fromEntries(
                  Object.entries(isRemovingItem).filter(([key]) => key.startsWith(`${categoryId}_`))
                )}
                isTogglingItems={Object.fromEntries(
                  Object.entries(isTogglingItem).filter(([key]) => key.startsWith(`${categoryId}_`))
                )}
              />
            ))}
        </div>

        <button 
          className="btn btn-secondary btn-full add-category-btn" 
          onClick={addNewCategoryBlock} 
          disabled={isAddingCategory || isDeleted}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M2 12h20"></path>
          </svg>
          <span>{isAddingCategory ? 'Додавання...' : 'Додати категорію'}</span>
        </button>

        <ExpensesSummary
          currentList={currentList}
          categoryTotals={categoryTotals}
          grandTotal={grandTotal}
          receiptStatus={receiptStatus}
          onSubmit={submitReceipt}
          isSubmitting={isSubmitting || isDeleted}
          clientVersion={clientVersion}
          serverVersion={serverVersion}
        />
      </div>
    </div>
  );
};

export default ShoppingListPage;
